<template>
    <div class="m-auto w-full h-full">
        <HomePageBackground v-if="qrCode.homepage"/>

        <div class="flex flex-col-reverse md:flex-row w-full h-full">
            <div v-if="qrCode.homepage" class="bg-white flex flex-col space-y-2 mr-4">
                <div class="order-last md:order-first hidden md:block">
                    <div class="text-lg font-semibold" v-text="qrCode.homepage.title"/>
                    <div class="font-light text-slate-900" v-text="qrCode.homepage.sub_title"/>
                </div>

                <Popover class="relative">
                    <PopoverButton class="rounded-xl bg-slate-600 hover:bg-black transition px-4 py-5 sm:p-6 w-full inline-flex justify-between leading-6 text-white whitespace-nowrap">
                        <span v-text="trans('homepage.add-content')"/>
                        <QrIcon icon="box-open-full" class="ml-2 h-6 w-6" aria-hidden="true" />
                    </PopoverButton>

                    <transition
                        enter-active-class="transition ease-out duration-200"
                        enter-from-class="opacity-0 translate-y-1"
                        enter-to-class="opacity-100 translate-y-0"
                        leave-active-class="transition ease-in duration-150"
                        leave-from-class="opacity-100 translate-y-0"
                        leave-to-class="opacity-0 translate-y-1"
                    >
                        <PopoverPanel v-slot="{ close }" class="absolute -left-8 z-10 mt-2 flex w-screen max-w-max translate-x-0 px-8">
                            <div class="w-screen max-w-md flex-auto overflow-hidden rounded-xl bg-white text-sm leading-6 shadow-lg ring-1 ring-gray-900/5">
                                <div class="p-4">
                                    <HomePageHeader @close="close()"/>
                                    <HomePageLink @close="close()"/>
                                    <CreateHomePageGallery @close="close()"/>
                                    <HomePageVideo @close="close()"/>
                                    <CreateHomePageDocument @close="close()"/>
                                </div>
                            </div>
                        </PopoverPanel>
                    </transition>
                </Popover>

                <Draggable
                    v-if="qrCode.homepage"
                    :list="qrCode.homepage.contents"
                    group="content"
                    handle=".handle"
                    class="space-y-2"
                    @start="isDragging = true"
                    @end="dragEnd"
                    item-key="id"
                >
                    <template #item="{ element }">
                        <QrCard gray no-shadow>
                            <QrParagraph class="text-xl mb-2 flex flex-row justify-between">
                                <div>
                                    <QrIcon icon="grip-dots" class="w-5 h-5 relative top-0.5 handle cursor-move"/>
                                    {{ typeTranslated(element.type) }}
                                </div>
                                <HomePageHeader v-if="element.type === 'header'" :homepage-content="element"/>
                                <HomePageLink v-else-if="element.type === 'link'" :homepage-content="element"/>
                                <HomePageVideo v-else-if="element.type === 'video'" :homepage-content="element"/>
                                <HomePageDocument v-else-if="element.type === 'document'" :homepage-content="element"/>
                                <HomePageGallery v-else-if="element.type === 'images'" :homepage-content="element" :key="element"/>
                            </QrParagraph>

                            <div class="flex space-x-4">
                                <div class="flex flex-col flex-auto max-w-[250px] md:w-[250px]">
                                    <QrParagraph :text="element.menu_button_name"/>

                                    <a
                                        v-if="element.type === 'document' && element.file"
                                        :href="element.file.file"
                                        target="_blank"
                                        class="text-slate-600 truncate"
                                    >
                                        <QrIcon icon="download"/>
                                        {{ element.file?.name }}
                                    </a>
                                    <a
                                        v-else-if="element.type === 'link'"
                                        :href="element.content.text"
                                        target="_blank"
                                        class="text-slate-600 truncate"
                                    >
                                        <QrIcon icon="up-right-from-square"/>
                                        {{ element.content.text }}
                                    </a>
                                    <p class="text-slate-600"
                                       v-else-if="element.files.length"
                                       v-text="trans('label.:images-images', {images: element.files.length})"
                                    />
                                    <p
                                        v-else-if="element.content && element.content.text"
                                        class="text-slate-600 truncate"
                                        v-text="element.content.text"
                                    />
                                </div>
                            </div>
                        </QrCard>
                    </template>
                </Draggable>
            </div>
            <div class="w-full h-full flex">
                <HomePageMainImage/>
            </div>
            <div class="block md:hidden p-4 mx-auto">
                <div class="text-lg font-semibold" v-text="qrCode.homepage.title"/>
                <div class="font-light text-slate-900" v-text="qrCode.homepage.sub_title"/>
            </div>
        </div>
    </div>
</template>

<script setup>
import { trans } from "@/libs/helper.js";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import HomePageLink from "@/components/qr-code/homepage/HomePageLink.vue";
import HomePageVideo from "@/components/qr-code/homepage/HomePageVideo.vue";
import HomePageDocument from "@/components/qr-code/homepage/HomePageDocument.vue";
import HomePageGallery from "@/components/qr-code/homepage/HomePageGallery.vue";
import QrCard from "@/components/application-ui/panels/QrCard.vue";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import { useQrCodesStore } from "@/store/qrCodes.js";
import { storeToRefs } from "pinia";
import HomePageMainImage from "@/components/qr-code/homepage/HomePageMainImage.vue";
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue';
import Draggable from "vuedraggable";
import { ref } from "vue";
import { useQrCodeHomepageStore } from "@/store/qrCodeHomepage.js";
import CreateHomePageGallery from "@/components/qr-code/homepage/CreateHomePageGallery.vue";
import CreateHomePageDocument from "@/components/qr-code/homepage/CreateHomePageDocument.vue";
import HomePageHeader from "@/components/qr-code/homepage/HomePageHeader.vue";
import HomePageBackground from "@/components/qr-code/homepage/HomePageBackground.vue";

const qrCodeStore = useQrCodesStore();
const { qrCode } = storeToRefs(qrCodeStore);
const homepageStore = useQrCodeHomepageStore();
const isDragging = ref(false);

const typeTranslated = (type) => {
    if (type === 'header') {
        return trans('homepage.header')
    }

    if (type === 'link') {
        return trans('homepage.website')
    }

    if (type === 'video') {
        return trans('homepage.video')
    }

    if (type === 'document') {
        return trans('homepage.document')
    }

    if (type === 'images') {
        return trans('homepage.images')
    }
};

const dragEnd = () => {
    isDragging.value = false;
    homepageStore.setContentsOrder();
};
</script>
